import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ordersAPI } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { CheckCircle, Package } from 'lucide-react';

export const OrderConfirmationPage = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  // 1. Define the function using useCallback to prevent infinite loops
  const loadOrder = useCallback(async () => {
    try {
      // Ensure we have an ID before calling API
      if (!orderId) return; 
      
      const data = await ordersAPI.getById(orderId);
      setOrder(data);
    } catch (error) {
      console.error('Failed to load order:', error);
    } finally {
      setLoading(false);
    }
  }, [orderId]); // Re-create function only if orderId changes

  // 2. Call the function inside useEffect
  useEffect(() => {
    loadOrder();
  }, [loadOrder]); // Safe to include loadOrder now

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-muted-foreground">Order not found</p>
      </div>
    );
  }

  // --- HELPER: Safely get address fields (Works for Woo or Mongo data) ---
  const shipping = order.shipping || order.billing || order.shipping_address || {};
  const fullName = shipping.fullName || `${shipping.first_name || ''} ${shipping.last_name || ''}`.trim() || 'Guest';
  const addressLine = shipping.address || shipping.address_1 || '';
  const city = shipping.city || '';
  const state = shipping.state || '';
  const zip = shipping.zipCode || shipping.postcode || '';
  const country = shipping.country || 'IN';

  return (
    <div className="min-h-screen py-16" data-testid="order-confirmation-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="flex justify-center mb-6">
          <CheckCircle className="w-20 h-20 text-green-500" />
        </div>

        <h1 className="font-outfit font-bold text-4xl mb-4" data-testid="order-success-heading">
          Order Placed Successfully!
        </h1>
        <p className="text-muted-foreground mb-8">
          Thank you for your order.
        </p>

        <div className="bg-white rounded-lg border border-border p-8 space-y-6 text-left" data-testid="order-details">
          <div className="flex items-center justify-between pb-6 border-b border-border">
            <div>
              <p className="text-sm text-muted-foreground">Order Number</p>
              <p className="font-mono font-bold text-lg" data-testid="order-id">
                #{order.wordpress_order_id || order.id}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Status</p>
              <p className="font-mono font-bold capitalize" data-testid="tracking-id">
                {order.status}
              </p>
            </div>
          </div>

          <div>
            <h3 className="font-semibold mb-3">Order Items</h3>
            <div className="space-y-3">
              {order.items.map((item, index) => {
                // Handle different price structures safely
                const price = item.product_price || item.product?.price || 0;
                
                return (
                  <div key={item.id || index} className="flex justify-between">
                    <span>
                        {item.product_name || item.product?.name || "Product"} x {item.quantity}
                    </span>
                    <span>₹{Number(price).toFixed(2)}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="space-y-2 pt-6 border-t border-border">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total Amount</span>
              <span className="font-bold text-xl" data-testid="order-total">
                ₹{Number(order.total).toFixed(2)}
              </span>
            </div>
          </div>

          <div className="bg-muted p-4 rounded-lg">
            <h4 className="font-semibold mb-2">Shipping Address</h4>
            <p className="text-sm text-muted-foreground">
              {fullName}<br />
              {addressLine}<br />
              {city}, {state} {zip}<br />
              {country}
            </p>
          </div>
        </div>

        <div className="flex gap-4 justify-center mt-8">
          <Link to="/orders">
            <Button variant="outline" className="rounded-full" data-testid="view-orders-button">
              <Package className="w-4 h-4 mr-2" />
              View All Orders
            </Button>
          </Link>
          <Link to="/products">
            <Button className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90" data-testid="continue-shopping-button">
              Continue Shopping
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};